/**
 * Forensic telemetry — high-volume, lossy-tolerant client timeline events.
 *
 * A dedicated lane on the SAME AppSync `device-events/{deviceId}` channel used by
 * deviceEvents.js, but for high-volume forensic events (shutter taps, capture
 * results, upload & analysis lifecycle). Every type is prefixed `fx_` so the
 * EventBridge rule `bronze-device-events-forensic-to-firehose` routes them to
 * Firehose -> S3 -> Athena (`bronze_tracker_forensic.events`), while operational
 * events (camera_error, upload_stuck, …) keep their CloudWatch-Logs path.
 *
 * Why a separate module from deviceEvents.js:
 *   - NO 1/min-per-type throttle — that gate is right for operational alerts but
 *     would drop almost every capture event.
 *   - Batched: events buffer and flush in small chunks so the network cost is a
 *     couple of tiny posts per order, not one request per event.
 *
 * Discipline: fire-and-forget, errors swallowed, bounded buffer (drop oldest when
 * long-offline), never blocks or throws into the capture/upload UX, never competes
 * with the photo upload queue. Losing forensic events is acceptable; a stuck tablet
 * is not. No persistence — the tail on an abrupt close is expendable.
 *
 * The payload travels as a JSON *string* so the Athena/Glue schema stays stable as
 * event props evolve (queried with json_extract). `device_id` / `restaurant_id` /
 * `received_at` are added server-side by the onPublish handler (identity-pinned),
 * so they are never sent from here.
 */
import { events } from 'aws-amplify/data';
import { v4 as uuidv4 } from 'uuid';
import Logger from '@/logger';
import packageInfo from '../package.json';
import { liveDemoOn } from '@/liveDemo/flag';

const logger = new Logger({ screen: 'forensicEvents' });

/** performance.now() where available; Date-based fallback keeps telemetry from ever throwing. */
function nowMs() {
  try {
    return typeof performance !== 'undefined' && performance.now ? performance.now() : 0;
  } catch (e) {
    return 0;
  }
}

const MAX_PER_POST = 5;        // AppSync Events publish cap (events per post)
const MAX_PENDING = 500;       // bounded buffer — drop oldest when long-offline
const FLUSH_AT = 20;           // flush once this many events are buffered
const FLUSH_AFTER_MS = 15000;  // ...or this long after the first buffered event
const APP_NAME = 'tracker-web'; // which app emitted the event (only emitter today; future-proofs multi-app)

let deviceId = null;
let restaurantId = null;
let sessionId = null;
let appVersion = null;
let appOrigin = null;
let pending = [];
let flushTimer = null;
let flushing = false;

// Identity of THIS document, minted when the module loads and dead when the page goes away.
//
// It is not a duplicate of session_id: the forensic session is minted at login and torn down at
// logout/expiry, so a re-login without a reload starts a new session inside the same page — and
// the question we most need to answer ("does the 40th photo of a shift cost more than the 1st?")
// is a property of the PAGE, not of the login. Grouping a degradation curve by session_id would
// silently cut it in half every time a token expires.
const pageId = uuidv4();
// Photos captured since this document loaded. Lives here, in module scope, and NOT in
// CameraPreviewComponent: that component unmounts every time the operator opens Settings or Help,
// which would reset the counter mid-shift and erase exactly the trend we are looking for.
let photosSinceLoad = 0;

/** Called once per successful capture. The count travels on every event, not just captures. */
export function notePhotoCaptured() {
  photosSinceLoad += 1;
  return photosSinceLoad;
}

export function getPhotosSinceLoad() {
  return photosSinceLoad;
}

export function getPageId() {
  return pageId;
}

export function initForensic(ids) {
  deviceId = ids.deviceId;
  restaurantId = ids.restaurantId;
  sessionId = uuidv4();
  appVersion = `${packageInfo.versionInternal ?? packageInfo.version}`;
  // Deployment origin (hostname) — the segmentation key that separates the production
  // domain from Amplify preview subdomains, so test/preview traffic can be filtered out of
  // production analysis. app_version alone can't (a preview and prod can share a version).
  appOrigin = (typeof window !== 'undefined' && window.location) ? window.location.hostname : null;
  pending = [];
}

export function stopForensic() {
  flush(); // best-effort final drain (not awaited by design)
  deviceId = null;
  restaurantId = null;
  sessionId = null;
  appOrigin = null;
  clearFlushTimer();
  pending = [];
}

/**
 * The current forensic session id, or null before login / after sign-out. Callers that emit
 * once-per-session events key their dedup on this so they neither burn the report on a
 * pre-login attempt (when reportForensicEvent is a no-op) nor stay silent after a re-login
 * mints a new session.
 */
export function getForensicSessionId() {
  return sessionId;
}

/**
 * Mint a correlation id shared by every event of one shutter interaction, so a
 * tap can be paired to its terminal result (saved / black-rejected / cooldown-
 * dropped) — a tap with no terminal is the blind spot this whole lane exists for.
 */
export function newInteractionId() {
  return uuidv4();
}

/**
 * Record one forensic event. `event` is the descriptive name WITHOUT the `fx_`
 * prefix (added here). `props` becomes the JSON-string payload (+ session_id,
 * app_version). Fire-and-forget; safe to call from any hot path.
 */
export function reportForensicEvent(event, props = {}) {
  if (!deviceId || !event) return;
  // Live sales demo on: nothing the presenter does is telemetry (src/liveDemo/).
  if (liveDemoOn()) return;
  try {
    pending.push({
      type: `fx_${event}`,
      severity: 'info',
      restaurant_id: restaurantId,
      ts: new Date().toISOString(),
      payload: JSON.stringify({
        ...props,
        session_id: sessionId,
        // Correlation keys added here rather than at each call site, so every event — including
        // the ones written before this existed — can be placed on the degradation curve.
        // since_load_ms orders events within a page without trusting the tablet's wall clock
        // (`ts`) or the ingestion time (`dt`), neither of which is reliable on this fleet.
        page_id: pageId,
        since_load_ms: Math.round(nowMs()),
        photos_since_load: photosSinceLoad,
        app: APP_NAME,
        app_version: appVersion,
        origin: appOrigin,
      }),
    });
    if (pending.length > MAX_PENDING) {
      pending = pending.slice(pending.length - MAX_PENDING);
    }
    if (pending.length >= FLUSH_AT) flush();
    else armFlushTimer();
  } catch (error) {
    // Telemetry must never break the caller.
    try { logger.console('reportForensicEvent failed', error); } catch (e) { /* noop */ }
  }
}

function armFlushTimer() {
  if (flushTimer) return; // timer counts from the FIRST buffered event — don't reset
  flushTimer = setTimeout(() => { flushTimer = null; flush(); }, FLUSH_AFTER_MS);
}

function clearFlushTimer() {
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
}

async function flush() {
  if (flushing || !deviceId || pending.length === 0) return;
  flushing = true;
  clearFlushTimer();
  try {
    while (pending.length > 0) {
      const batch = pending.slice(0, MAX_PER_POST);
      await events.post(`device-events/${deviceId}`, batch);
      pending = pending.slice(batch.length);
    }
  } catch (error) {
    // Best-effort: keep the (bounded) buffer and let the next event / timer retry.
    // Forensic loss is acceptable — never block or throw.
    try { logger.console('forensic flush failed, will retry', error); } catch (e) { /* noop */ }
    armFlushTimer();
  } finally {
    flushing = false;
  }
}

// Lifecycle flushes — best-effort attempt to capture the tail before the page
// goes away. An in-flight post may not survive an abrupt close; that is fine.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => flush());
}
